'use client'
import React, { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import useSWR, { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { fetcher, putFetcher, delFetcher } from 'api/swr'
import { optimisticRemoveComponentsByLid } from 'api'
import {
  jobMatchesLayer,
  useCptUploadStore,
} from 'Editor/BottomPanel/BatchUpload/uploadQueue'
import { displayedPickerCards } from 'Editor/lib/visibleComponents'
import {
  CopyIcon,
  DotsHorizontalIcon,
  TrashIcon,
  Component1Icon,
  Component2Icon,
} from '@radix-ui/react-icons'
import Skeleton from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogDescription,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import Loader from '@/components/ui/Loader'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast, useToast } from '@/components/ui/use-toast'
import { LayerCoverPicker } from 'components/LayerCoverPicker'
import { layerIcon } from 'commons/cdn'
import ImgWithState from 'components/ui/ImgWithState'
import { useForm } from './index'

export function DataTableRowActions({ row }) {
  const layer = row.original

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[160px]">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => navigator.clipboard.writeText(layer.id)}
        >
          <CopyIcon className="mr-2 h-4 w-4" /> Copy layer ID
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {row.original.id !== 'draft' && <DeleteLayer layer={layer} />}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const DeleteLayer = ({ layer }) => {
  const { tid } = useParams()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const { trigger, isMutating } = useSWRMutation('/layers', delFetcher, {
    onSuccess: () => {
      mutate(['/layers', { tid }])
      optimisticRemoveComponentsByLid(tid, layer.id)
      setOpen(false)
      toast({
        title: 'Layer Deleted',
        description: 'The layer has been successfully deleted.',
        className: 'bg-green-500 text-white',
      })
    },
  })

  const handleDelete = async () => {
    if (isMutating) return
    await trigger({ id: layer.id, body: { tid } })
    const { jobs, removeJob } = useCptUploadStore.getState()
    jobs
      .filter((job) => job.tid === tid && jobMatchesLayer(job, layer.id))
      .forEach((job) => removeJob(job.id))
  }

  return (
    <>
      <DropdownMenuItem
        className="text-destructive focus:bg-destructive focus:text-destructive-foreground"
        onSelect={(e) => {
          e.preventDefault()
          setOpen(true)
        }}
      >
        <TrashIcon className="mr-2 h-4 w-4" />
        Delete
        <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
      </DropdownMenuItem>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (isMutating) return
          setOpen(next)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this layer?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete “{layer.name || layer.id}”?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              disabled={isMutating}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isMutating}
              onClick={handleDelete}
            >
              {isMutating ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const DataTableRowOpt = ({ row }) => {
  const { tid } = useParams()
  const layer = row.original
  const { trigger, isMutating } = useSWRMutation(
    `/layers/${layer.id}`,
    putFetcher,
  )

  const onCheckedChange = (value) => {
    if (isMutating) return
    trigger(
      { tid, key: 'optional', value: !value },
      {
        onSuccess: () => {
          mutate(['/layers', { tid }])
        },
      },
    )
  }

  return (
    <div className="flex justify-center items-center">
      {isMutating ? (
        <Loader className="w-4 h-4" />
      ) : (
        <>
          <Checkbox
            className="border-black/60 data-[state=checked]:border-primary"
            checked={!layer.opt}
            onCheckedChange={onCheckedChange}
            aria-label="Select opt"
          />
        </>
      )}
    </div>
  )
}

export const DataTableRowCover = ({ row }) => {
  const { tid } = useParams()
  const data = row.original

  const [cover, setCover] = useState(data.cover || '')

  const { trigger, isMutating } = useSWRMutation(
    `/layers/${data.id}`,
    putFetcher,
    {
      onSuccess: () => {
        toast({
          title: 'Cover Updated',
          description: 'The cover has been successfully updated.',
          className: 'bg-green-500 text-white',
        })
        mutate(['/layers', { tid }])
      },
    },
  )

  const onSubmit = useCallback(
    (v) => {
      const { id, name, p, z, gid, max, body, water, background, opt } = v
      setCover(id)
      const data = {
        tid,
        key: 'form',
        value: {
          cover: id,
          name: name || 'new layer',
          playIndex: p || 0,
          zIndex: z || 0,
          maxChoose: max || 1,
          optional: opt ? true : false,
        },
      }
      if (gid) data.value['gid'] = gid
      if (body) data.value['bodymark'] = body
      if (water) data.value['watermark'] = water
      if (background) data.value['background'] = background
      trigger(data)
    },
    [trigger, tid],
  )

  return (
    <div className="w-16 h-16">
      <LayerCoverPicker currCover={{ id: cover }} onSubmit={onSubmit}>
        {cover ? (
          <ImgWithState
            isMutating={isMutating}
            src={layerIcon(cover)}
          />
        ) : (
          <Skeleton className="w-16 h-16 rounded-xl" />
        )}
      </LayerCoverPicker>
    </div>
  )
}

export const DataTableRowText = ({ row, entityKey, type }) => {
  const { tid } = useParams()
  const layer = row.original
  const [open, setOpen] = useState(false)
  const { data } = useForm((state) => state)
  const duplicate = data.filter((item) => item[entityKey] === layer[entityKey])

  const { trigger, isMutating } = useSWRMutation(
    `/layers/${layer.id}`,
    putFetcher,
  )

  const [text, setText] = useState(layer?.[entityKey] || '')

  const disabled = !text || isMutating || layer?.[entityKey] === text
  const onSubmit = () => {
    if (disabled) {
      return null
    }
    trigger(
      { tid, key: entityKey, value: text },
      {
        onSuccess: () => {
          mutate(['/layers', { tid }])
          setOpen(false)
        },
      },
    )
  }

  return (
    <div className="max-w-[500px] min-w-[80px] flex flex-col gap-2">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <div
            className={cn(
              'flex h-8 w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm transition-colors',
              'hover:outline-none hover:ring-1 hover:ring-ring hover:border-input hover:shadow-sm',
              `${duplicate.length > 1 ? 'text-red-600' : ''}`,
            )}
          >
            {layer?.[entityKey]}
          </div>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update {entityKey}</DialogTitle>
            <DialogDescription>update layer {entityKey}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {entityKey}
              </Label>
              <Input
                className="col-span-3"
                value={text}
                type={type}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={isMutating || !text || text === layer?.[entityKey]}
              onClick={onSubmit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const ComponentCount = ({ row }) => {
  const layer = row.original
  const { data } = useSWR(['/components', { tid: layer.tid }], fetcher)
  const { data: dlcs } = useSWR(['/dlcs', { tid: layer.tid }], fetcher)
  const visible = displayedPickerCards(data, layer.tid, dlcs, layer.id)
  const aiCpts = visible.filter((cpt) => cpt.ai === 1)
  return (
    <div className="flex items-center px-2 text-sm text-black/60">
      <Component1Icon className="mr-2 w-4 h-4" />{' '}
      <span className="me-4">{layer.cptCount ?? visible.length}</span>
      <Component2Icon className="mr-2 w-4 h-4" /> <span>{aiCpts.length}</span>
    </div>
  )
}

export const CopyId = ({ row }) => {
  return (
    <div
      className="flex items-center px-2 mt-2 text-sm text-black/60"
      onClick={() => navigator.clipboard.writeText(row.original.id)}
    >
      <CopyIcon className="mr-2 h-4 w-4" />
      {row.original.id}
    </div>
  )
}
